import { motion } from "framer-motion";
import { Clock, XCircle, AlertTriangle, HandCoins, Hourglass, EyeOff, Zap, ShieldCheck, BadgeCheck, Sparkles, Cpu, Rocket } from "lucide-react";

const problems = [
  { icon: Hourglass, t: "Endless Waiting", d: "Weeks of listings, calls, and dead ends." },
  { icon: AlertTriangle, t: "Uncertain Pricing", d: "Guesswork instead of data-backed value." },
  { icon: EyeOff, t: "Fake Buyers", d: "Time-wasters and bots flood every listing." },
  { icon: HandCoins, t: "Lowball Offers", d: "Endless bargaining for what you deserve." },
  { icon: Clock, t: "Time-Consuming Negotiations", d: "Negotiations that drag on for weeks." },
  { icon: XCircle, t: "Zero Transparency", d: "Hidden fees, surprise deductions, no clarity." },
];

const solutions = [
  { icon: Cpu, t: "Instant Smart Valuation", d: "Exact buying price in under 10 minutes." },
  { icon: Sparkles, t: "Exact Buying Price", d: "Real-time market data, not opinions." },
  { icon: ShieldCheck, t: "Transparent Pricing", d: "Every signal explained. No hidden math." },
  { icon: Zap, t: "Fast Selling Process", d: "Schedule, inspect, sell. Done." },
  { icon: Rocket, t: "No Unnecessary Waiting", d: "Skip the calls. Skip the chase." },
  { icon: BadgeCheck, t: "Buy, Sell or Exchange", d: "Upgrade to a bigger and better machine — seamlessly." },
];

export default function ProblemSolution() {
  return (
    <section data-testid="problem-solution-section" className="relative py-24 md:py-36 bg-[#0D0F12]">
      <div className="absolute inset-0 grid-bg opacity-10" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-xs font-mono tracking-[0.3em] uppercase text-[#00cccc]">// The Shift</span>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black leading-tight tracking-tight">
            The Old Way Is <span className="text-zinc-500 line-through decoration-zinc-700">Broken.</span>
            <br /> Biker Boulevard <span className="text-gradient-cyan">Fixes It.</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Problems */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative p-6 md:p-8 rounded-2xl border border-white/10 bg-white/[0.015]"
          >
            <div className="flex items-center gap-3 mb-7">
              <span className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-500">// Traditional Selling</span>
              <span className="flex-1 h-[1px] bg-zinc-800" />
            </div>
            <div className="space-y-4">
              {problems.map((p, i) => (
                <motion.div
                  key={p.t}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-4 p-4 rounded-xl bg-zinc-900/30 border border-zinc-800/50"
                >
                  <p.icon className="w-5 h-5 text-zinc-500 mt-0.5 shrink-0" />
                  <div>
                    <h3 className="text-base font-semibold text-zinc-300">{p.t}</h3>
                    <p className="text-sm text-zinc-500 mt-1">{p.d}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Solutions */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative p-6 md:p-8 rounded-2xl glass neon-glow"
          >
            <div className="flex items-center gap-3 mb-7">
              <span className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#00cccc]">// Biker Boulevard</span>
              <span className="flex-1 h-[1px] bg-gradient-to-r from-[#00cccc]/40 to-transparent" />
            </div>
            <div className="space-y-4">
              {solutions.map((s, i) => (
                <motion.div
                  key={s.t}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-4 p-4 rounded-xl bg-[#00cccc]/[0.04] border border-[#00cccc]/15 hover:border-[#00cccc]/40 transition-all"
                  data-testid={`solution-card-${i}`}
                >
                  <s.icon className="w-5 h-5 text-[#00cccc] mt-0.5 shrink-0" />
                  <div>
                    <h3 className="text-base font-semibold text-white">{s.t}</h3>
                    <p className="text-sm text-zinc-400 mt-1">{s.d}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
